import { useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";

import { getCurrentReinsuranceYear } from "@datanac/datanac-api-toolkit/dist/utility/InsurancePlanHelper";
import { useDialog } from "@datanac/datanac-mui-toolkit";
import { useCardController } from "@datanac/datanac-mui-toolkit/dist/hooks";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import EastOutlined from "@mui/icons-material/EastOutlined";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { green } from "@mui/material/colors";
import { UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { getBudgetYears, getHistoryYears } from "components/Budget/helpers";
import EmptyCard from "components/Cards/EmptyCard";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import { useSnackbar } from "notistack";

import { getUniqueCounties } from "../../helpers";
import FarmCountyCard from "../FarmCountyCard";

function mergeCountySummaries(plantedCounties = [], baseCounties = []) {
  const byKey = new Map();
  const keyOf = (county) => `${county.location_state_name}|${county.location_county_name}`;

  plantedCounties.forEach((county) => {
    byKey.set(keyOf(county), { ...county, base_acreage: 0 });
  });

  baseCounties.forEach((county) => {
    const key = keyOf(county);
    const existing = byKey.get(key);
    if (existing) {
      existing.base_acreage = county.base_acreage;
      existing.unit_count = Math.max(Number(existing.unit_count) || 0, Number(county.unit_count) || 0);
    } else {
      byKey.set(key, { ...county, reported_acreage: county.reported_acreage || 0 });
    }
  });

  return [...byKey.values()];
}

export default function ProducerFarmCopyDialog({ onClose }) {
  const { globalState } = useContext(AppContext);

  const { component: confirmDialogComponent } = useDialog();
  const { enqueueSnackbar } = useSnackbar();

  // ---

  const [state, dispatch] = useReducer(propUpdateReducer, {
    crop_year: getCurrentReinsuranceYear() - 1,
    new_crop_year: getCurrentReinsuranceYear(),
    copy_planted_acres: true,
    copy_base_acres: false,
  });
  const handleChangeStateSelect = (event) => {
    dispatch({
      type: "update",
      payload: { key: event.target.name, value: event.target.value },
    });
  };

  const handleChangeStateCheckbox = (event) => {
    dispatch({
      type: "update",
      payload: { key: event.target.name, value: event.target.checked },
    });
  };

  const yearsValid = useMemo(() => {
    return state?.crop_year && state?.new_crop_year && state?.new_crop_year !== state?.crop_year;
  }, [state?.crop_year, state?.new_crop_year]);

  const copySelectionValid = useMemo(() => {
    return state?.copy_planted_acres || state?.copy_base_acres;
  }, [state?.copy_planted_acres, state?.copy_base_acres]);

  const isValid = useMemo(() => {
    return yearsValid && copySelectionValid;
  }, [yearsValid, copySelectionValid]);

  const validationErrors = useMemo(() => {
    const _validationErrors = [];
    if (state.crop_year === state.new_crop_year) {
      _validationErrors.push('"From" and "To" years must be different.  Please choose another year.');
    }
    if (yearsValid && !copySelectionValid) {
      _validationErrors.push('Select at least one data type to copy: "Planted Acres" or "Base Acres".');
    }

    return _validationErrors;
  }, [state.crop_year, state.new_crop_year, yearsValid, copySelectionValid]);

  const [isLoadingAction, setIsLoadingAction] = useState(false);
  const handleActionClick = () => {
    if (isValid) {
      setIsLoadingAction(true);

      const _parms = {
        producer_token: globalState?.producer_token,
        crop_year: state?.crop_year,
        new_crop_year: state?.new_crop_year,
        copy_planted_acres: state?.copy_planted_acres,
        copy_base_acres: state?.copy_base_acres,
      };
      UsersApiHelper.updateProducerFarmCopyCropYear(_parms)
        .then(() => {
          setIsLoadingAction(false);
          onClose?.(state?.new_crop_year);
        })
        .catch((err) => {
          setIsLoadingAction(false);
          console.warn(err);
        });
    }
  };

  // ---

  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const { state: history, actions: historyActions } = useCardController();
  const loadGenRef = useRef(0);

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const loadHistory = async () => {
    const gen = ++loadGenRef.current;
    try {
      const promises = [];

      if (state.copy_planted_acres) {
        const producerFilter = {
          producer_token: globalState.producer_token,
          crop_year: state?.crop_year,
          is_active: true,
        };
        promises.push(
          UsersApiHelper.selectProducerFarms(producerFilter, globalState).then((farms) => getUniqueCounties(farms))
        );
      } else {
        promises.push(Promise.resolve([]));
      }

      if (state.copy_base_acres) {
        const baseAcresFilter = {
          producer_token: globalState.producer_token,
          crop_year: state?.crop_year,
          is_active: true,
        };
        promises.push(
          UsersApiHelper.selectProducerFarmBaseAcres(baseAcresFilter, globalState).then((baseAcres) => {
            const counties = getUniqueCounties(baseAcres);
            const farmsByCounty = {};
            baseAcres.forEach((ba) => {
              const key = `${ba.location_state_name}|${ba.location_county_name}`;
              if (!farmsByCounty[key]) farmsByCounty[key] = new Set();
              farmsByCounty[key].add(ba.producer_farm_id);
            });
            return counties.map((c) => ({
              ...c,
              unit_count: farmsByCounty[`${c.location_state_name}|${c.location_county_name}`]?.size ?? c.unit_count,
            }));
          })
        );
      } else {
        promises.push(Promise.resolve([]));
      }

      const [plantedCounties, baseCounties] = await Promise.all(promises);
      if (gen !== loadGenRef.current) return;
      historyActions.replace(mergeCountySummaries(plantedCounties, baseCounties));

      setIsLoadingHistory(false);
    } catch (err) {
      if (gen !== loadGenRef.current) return;
      setIsLoadingHistory(false);
      historyActions.replace([]);
      handleError(err);
    }
  };

  useEffect(() => {
    if (yearsValid && copySelectionValid && state.crop_year) {
      setIsLoadingHistory(true);
      loadHistory();
    } else {
      historyActions.replace([]);
      setIsLoadingHistory(false);
    }
  }, [state.crop_year, state.copy_planted_acres, state.copy_base_acres, yearsValid, copySelectionValid]);

  // ---

  return (
    <>
      <DialogTitle>Copy Prior Season</DialogTitle>
      <DialogContent>
        <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "end" }}>
          <Box sx={{ minWidth: "35%" }}>
            <FormControl fullWidth>
              <InputLabel id="crop_year_from">Copy From</InputLabel>
              <Select
                labelId="crop_year_from"
                value={state?.crop_year || ""}
                name={"crop_year"}
                onChange={handleChangeStateSelect}
                required={true}
              >
                {getHistoryYears()?.map((currentItem, index) => {
                  return (
                    <MenuItem key={index} value={currentItem}>
                      {currentItem}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>

          <EastOutlined sx={{ height: "3rem" }} />

          <Box sx={{ minWidth: "35%" }}>
            <FormControl fullWidth>
              <InputLabel id="crop_year_to">Copy To</InputLabel>
              <Select
                labelId="crop_year_to"
                value={state?.new_crop_year || ""}
                name={"new_crop_year"}
                onChange={handleChangeStateSelect}
                required={true}
              >
                {getBudgetYears()?.map((currentItem, index) => {
                  return (
                    <MenuItem key={index} value={currentItem}>
                      {currentItem}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
        </Stack>

        <FormGroup row sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                name="copy_planted_acres"
                checked={Boolean(state?.copy_planted_acres)}
                onChange={handleChangeStateCheckbox}
              />
            }
            label="Planted Acres"
          />
          <FormControlLabel
            control={
              <Checkbox
                name="copy_base_acres"
                checked={Boolean(state?.copy_base_acres)}
                onChange={handleChangeStateCheckbox}
              />
            }
            label="Base Acres"
          />
        </FormGroup>

        <List>
          {validationErrors.map((err, index) => (
            <ListItem key={index}>
              <ListItemAvatar>
                <ErrorOutline color="error" />
              </ListItemAvatar>
              <ListItemText>
                <Typography color="error">{err}</Typography>
              </ListItemText>
            </ListItem>
          ))}
        </List>

        {!history?.length && (
          <EmptyCard text="No history found" secondaryText="Please choose another year" isLoading={isLoadingHistory} />
        )}

        <Grid container spacing={1}>
          {history.map((currentCountyPractice, index) => (
            <Grid key={index} item xs={12} md={6}>
              <FarmCountyCard currentCounty={currentCountyPractice} isNavigation={false} />
            </Grid>
          ))}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
          <Button variant="contained" color="secondary" autoFocus onClick={() => onClose(null)}>
            <CloseOutlined sx={{ mr: 1 }} />
            Cancel
          </Button>

          <Box sx={{ position: "relative" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleActionClick}
              disabled={!isValid || isLoadingAction}
            >
              <ContentCopyOutlined sx={{ mr: 1 }} />
              Copy
            </Button>
            {isLoadingAction && (
              <CircularProgress
                size={24}
                sx={{
                  color: green[500],
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  marginTop: "-12px",
                  marginLeft: "-12px",
                }}
              />
            )}
          </Box>
        </Stack>
      </DialogActions>

      {confirmDialogComponent}
    </>
  );
}
